import { useState, useEffect, useCallback, useRef } from 'react';
import { menuItemsService } from '../lib/database';
import { liveDataStatus } from '../lib/liveDataStatus';
import { getDataSource } from '../lib/config';
import { menuItems as initialMenuItems } from '../data/menu';
import type { MenuItem } from '../types';

interface UseMenuItemsReturn {
  menuItems: MenuItem[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  create: (item: Omit<MenuItem, 'id'>) => Promise<MenuItem>;
  update: (id: number, updates: Partial<MenuItem>) => Promise<MenuItem>;
  remove: (id: number) => Promise<void>;
  getByCategory: (categoryId: number) => MenuItem[];
}

export const useMenuItems = (): UseMenuItemsReturn => {
  const [menuItems, setMenuItems] = useState<MenuItem[]>(initialMenuItems);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const unsubRef = useRef<(() => void) | null>(null);
  const gotFirstSnapshotRef = useRef(false);

  const subscribe = useCallback(() => {
    const dataSource = getDataSource();
    if (dataSource === 'mock') {
      setMenuItems(initialMenuItems);
      setLoading(false);
      gotFirstSnapshotRef.current = true;
      return () => {};
    }

    setLoading(!gotFirstSnapshotRef.current);
    setError(null);

    return menuItemsService.subscribe((data) => {
      gotFirstSnapshotRef.current = true;
      setMenuItems(data);
      setLoading(false);
      setError(null);
    });
  }, []);

  useEffect(() => {
    unsubRef.current = subscribe();
    return () => {
      if (unsubRef.current) {
        unsubRef.current();
        unsubRef.current = null;
      }
    };
  }, [subscribe]);

  const refresh = useCallback(async (): Promise<void> => {
    const feeds = liveDataStatus.getAll();
    if (feeds.some(f => f.name === 'menu_items')) {
      if (unsubRef.current) {
        unsubRef.current();
        unsubRef.current = null;
      }
      unsubRef.current = subscribe();
    }
  }, [subscribe]);

  const create = useCallback(async (item: Omit<MenuItem, 'id'>): Promise<MenuItem> => {
    const dataSource = getDataSource();
    if (dataSource === 'mock') {
      const newItem: MenuItem = {
        ...item,
        id: Math.max(...menuItems.map(m => m.id), 0) + 1,
      };
      setMenuItems(prev => [...prev, newItem]);
      return newItem;
    }

    setError(null);
    try {
      const newItem = await menuItemsService.create(item);
      // Optimistically merge - subscription will reconcile.
      setMenuItems(prev => prev.some(m => m.id === newItem.id) ? prev : [...prev, newItem]);
      return newItem;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create menu item';
      setError(errorMessage);
      throw err;
    }
  }, [menuItems]);

  const update = useCallback(async (id: number, updates: Partial<MenuItem>): Promise<MenuItem> => {
    const dataSource = getDataSource();
    if (dataSource === 'mock') {
      const updatedItem = menuItems.find(m => m.id === id);
      if (!updatedItem) throw new Error('Menu item not found');
      const newItem = { ...updatedItem, ...updates };
      setMenuItems(prev => prev.map(m => m.id === id ? newItem : m));
      return newItem;
    }

    setError(null);
    try {
      const updatedItem = await menuItemsService.update(id, updates);
      setMenuItems(prev => prev.map(m => m.id === id ? updatedItem : m));
      return updatedItem;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update menu item';
      setError(errorMessage);
      throw err;
    }
  }, [menuItems]);

  const remove = useCallback(async (id: number): Promise<void> => {
    const dataSource = getDataSource();
    if (dataSource === 'mock') {
      setMenuItems(prev => prev.filter(m => m.id !== id));
      return;
    }

    setError(null);
    try {
      await menuItemsService.delete(id);
      setMenuItems(prev => prev.filter(m => m.id !== id));
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete menu item';
      setError(errorMessage);
      throw err;
    }
  }, []);

  const getByCategory = useCallback((categoryId: number): MenuItem[] => {
    return menuItems.filter(item => item.categoryId === categoryId);
  }, [menuItems]);

  return {
    menuItems,
    loading,
    error,
    refresh,
    create,
    update,
    remove,
    getByCategory,
  };
};
